import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  KeyRound, RefreshCw, ShieldCheck, ShieldAlert, ShieldX,
  Apple, Wallet, Clock, ExternalLink, Hash, User2,
  Database, AlertCircle, History,
} from "lucide-react";
import { API } from "../../config";

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const fmtDays = (n) => {
  if (n === null || n === undefined) return "—";
  if (n < 0) return `scaduto da ${Math.abs(n)} giorni`;
  if (n === 0) return "scade oggi";
  return `${n} giorni`;
};

const SEV = {
  ok:    { Icon: ShieldCheck, color: "text-emerald-300", bg: "bg-emerald-500/10 border-emerald-500/30", label: "OK" },
  warn:  { Icon: ShieldAlert, color: "text-amber-300",   bg: "bg-amber-500/10 border-amber-500/30",    label: "Attenzione" },
  error: { Icon: ShieldX,     color: "text-red-300",     bg: "bg-red-500/10 border-red-500/30",        label: "Critico" },
};

const ACTION_LABEL = {
  "apple_wallet.credentials_upload":  "Apple .p12 caricato",
  "apple_wallet.credentials_delete":  "Apple .p12 rimosso",
  "google_wallet.credentials_upload": "Google JSON caricato",
};

const PlatformCard = ({ panel }) => {
  const stMeta = SEV[panel.storage.severity] || SEV.warn;
  const exMeta = panel.expiry.severity ? (SEV[panel.expiry.severity] || SEV.warn) : null;
  const StIcon = stMeta.Icon;
  const ExIcon = exMeta?.Icon;

  const PlatformIcon = panel.platform === "apple" ? Apple : Wallet;

  return (
    <div
      className="bg-[#0F0F0F] border border-gray-800 rounded-2xl p-5 flex flex-col gap-4"
      data-testid={`wcl-${panel.platform}-card`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <PlatformIcon className={`w-5 h-5 ${panel.platform === "apple" ? "text-gray-200" : "text-blue-300"}`} />
          <h4 className="text-white font-semibold text-sm truncate">{panel.label}</h4>
        </div>
        <a
          href={panel.rotate_url}
          target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-300 hover:text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-full px-3 py-1 whitespace-nowrap"
          data-testid={`wcl-${panel.platform}-rotate-btn`}
        >
          <KeyRound className="w-3 h-3" /> Rotate now
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {/* Storage status */}
      <div className={`border rounded-xl p-3 flex items-start gap-3 ${stMeta.bg}`}>
        <StIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${stMeta.color}`} />
        <div className="flex-1 min-w-0">
          <p className={`text-[11px] uppercase tracking-wider font-semibold ${stMeta.color}`}>
            Storage · {panel.storage.source} · {stMeta.label}
          </p>
          <p className="text-gray-300 text-xs mt-1 leading-relaxed">
            {panel.storage.message}
          </p>
        </div>
      </div>

      {/* Credentials metadata */}
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <div className="bg-[#141414] border border-gray-800 rounded-lg p-2.5">
          <div className="flex items-center gap-1.5 text-gray-500 mb-1">
            <Hash className="w-3 h-3" /> <span className="uppercase tracking-wider text-[10px]">SHA-256</span>
          </div>
          <p className="font-mono text-gray-200 truncate" title={panel.credentials.sha256 || ""}>
            {panel.credentials.sha256_short || "—"}
          </p>
        </div>
        <div className="bg-[#141414] border border-gray-800 rounded-lg p-2.5">
          <div className="flex items-center gap-1.5 text-gray-500 mb-1">
            <Database className="w-3 h-3" /> <span className="uppercase tracking-wider text-[10px]">Size</span>
          </div>
          <p className="font-mono text-gray-200">
            {panel.credentials.size_bytes ? `${panel.credentials.size_bytes} B` : "—"}
          </p>
        </div>
        <div className="bg-[#141414] border border-gray-800 rounded-lg p-2.5 col-span-2">
          <div className="flex items-center gap-1.5 text-gray-500 mb-1">
            <User2 className="w-3 h-3" /> <span className="uppercase tracking-wider text-[10px]">Ultima rotazione</span>
          </div>
          <p className="text-gray-200">
            <span className="font-mono text-gray-300">{panel.credentials.uploaded_by || "—"}</span>
            {panel.credentials.uploaded_at && (
              <span className="text-gray-500"> · {fmtDateTime(panel.credentials.uploaded_at)}</span>
            )}
          </p>
          {panel.platform === "google" && panel.credentials.client_email && (
            <p className="text-gray-500 text-[10px] mt-1 truncate" title={panel.credentials.client_email}>
              {panel.credentials.client_email}
            </p>
          )}
        </div>
      </div>

      {/* Expiry (Apple only — Google has no per-cert expiry) */}
      {panel.platform === "apple" && (
        exMeta ? (
          <div className={`border rounded-xl p-3 flex items-start gap-3 ${exMeta.bg}`}>
            <ExIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${exMeta.color}`} />
            <div className="flex-1">
              <p className={`text-[11px] uppercase tracking-wider font-semibold ${exMeta.color}`}>
                Expiry · {exMeta.label}
              </p>
              <p className="text-gray-300 text-xs mt-1">
                Scade tra <strong>{fmtDays(panel.expiry.days_remaining)}</strong>
                {panel.expiry.not_after_iso && (
                  <span className="text-gray-500"> · {fmtDateTime(panel.expiry.not_after_iso)}</span>
                )}
              </p>
              {panel.expiry.subject && (
                <p className="text-gray-500 text-[10px] mt-1 truncate" title={panel.expiry.subject}>
                  {panel.expiry.subject}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-[#141414] border border-gray-800 rounded-xl p-3 flex items-start gap-3">
            <Clock className="w-4 h-4 mt-0.5 text-gray-500" />
            <p className="text-gray-500 text-xs">
              Metadata cert non disponibili (il .p12 non è caricato o non è leggibile).
            </p>
          </div>
        )
      )}
      {panel.platform === "google" && (
        <div className="bg-[#141414] border border-gray-800 rounded-xl p-3 flex items-start gap-3">
          <Clock className="w-4 h-4 mt-0.5 text-gray-500" />
          <p className="text-gray-500 text-xs leading-relaxed">
            {panel.expiry.note || "Le credenziali Google Service Account non scadono automaticamente."}
          </p>
        </div>
      )}
    </div>
  );
};

export const WalletCredentialsLifecycleWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/admin/wallet/credentials-lifecycle`);
      setData(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === "string" ? d : (d?.msg || e?.message || "Errore"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const overallMeta = data ? (SEV[data.overall_severity] || SEV.warn) : null;
  const OverallIcon = overallMeta?.Icon;

  return (
    <div
      className="bg-[#0A0A0A] border border-gray-800 rounded-2xl p-6"
      data-testid="wallet-credentials-lifecycle-widget"
    >
      <div className="flex items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center flex-shrink-0">
            <KeyRound className="w-5 h-5 text-indigo-300" />
          </div>
          <div className="min-w-0">
            <h3 className="text-white font-semibold text-base">
              Wallet Credentials Lifecycle
            </h3>
            <p className="text-gray-500 text-xs">
              Stato unificato dei certificati Apple .p12 e service-account Google · ultima rotazione · scadenza · audit trail
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {data && OverallIcon && (
            <span
              className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1 rounded-full border ${overallMeta.bg} ${overallMeta.color}`}
              data-testid="wcl-overall-severity"
            >
              <OverallIcon className="w-3 h-3" /> {overallMeta.label.toUpperCase()}
            </span>
          )}
          <button
            type="button"
            onClick={fetch}
            disabled={loading}
            className="inline-flex items-center gap-1.5 bg-[#1A1A1A] hover:bg-[#222] border border-gray-700 text-gray-200 px-3 py-1.5 rounded-lg text-xs disabled:opacity-50"
            data-testid="wcl-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Aggiorna
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-red-300 text-xs">{error}</p>
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
            <PlatformCard panel={data.apple} />
            <PlatformCard panel={data.google} />
          </div>

          {/* Audit trail */}
          <div
            className="bg-[#0F0F0F] border border-gray-800 rounded-2xl p-4"
            data-testid="wcl-audit-log"
          >
            <div className="flex items-center gap-2 mb-3">
              <History className="w-4 h-4 text-gray-400" />
              <h4 className="text-gray-200 font-semibold text-sm">
                Audit log · ultime {data.audit_log.length} operazioni
              </h4>
            </div>
            {data.audit_log.length === 0 ? (
              <p className="text-gray-500 text-xs italic">
                Nessuna operazione di rotazione/upload registrata. Le rotazioni future appariranno qui.
              </p>
            ) : (
              <ul className="space-y-1.5 max-h-64 overflow-y-auto">
                {data.audit_log.map((entry, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-[11px] py-1.5 border-b border-gray-800/60 last:border-0"
                  >
                    <span className="text-gray-500 font-mono whitespace-nowrap mt-0.5">
                      {fmtDateTime(entry.at)}
                    </span>
                    <span className="text-gray-300 flex-1 min-w-0">
                      <span className="font-medium text-gray-100">
                        {ACTION_LABEL[entry.action] || entry.action}
                      </span>
                      {entry.actor_email && (
                        <span className="text-gray-500"> · {entry.actor_email}</span>
                      )}
                      {entry.metadata?.sha256 && (
                        <span className="ml-2 font-mono text-[10px] text-gray-500">
                          sha {entry.metadata.sha256.slice(0, 12)}…
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
};
